import { useEffect, useRef, useState, useCallback } from 'react';
import { io as ioClient, Socket } from 'socket.io-client';
import {
  useAppSelector,
  useAppDispatch,
  selectAccessToken,
  selectIsAuthenticated,
  baseApi,
  chatApi,
} from '@nimbazaar/store';
import type { MessageWithSender } from '@nimbazaar/types';

const API_URL = import.meta.env?.VITE_API_URL || 'http://localhost:3000';
const SOCKET_URL = API_URL.replace(/\/api\/?$/, '');

interface UseSocketReturn {
  socket: Socket | null;
  isConnected: boolean;
  onlineUsers: Set<string>;
  typingUsers: Map<string, string>;
}

let globalSocket: Socket | null = null;
let refCount = 0;

export function useSocket(): UseSocketReturn {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(selectAccessToken);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const [typingUsers, setTypingUsers] = useState<Map<string, string>>(new Map());
  const socketRef = useRef<Socket | null>(null);

  // Push a new message directly into the RTK cache (no refetch)
  const pushMessageToCache = useCallback(
    (message: MessageWithSender) => {
      // Update every matching getMessages cache entry for this conversation
      dispatch(
        chatApi.util.updateQueryData(
          'getMessages',
          { conversationId: message.conversationId, limit: 100 },
          (draft) => {
            // Avoid duplicates
            if (!draft.data.some((m) => m.id === message.id)) {
              draft.data.push(message);
            }
          }
        )
      );
    },
    [dispatch]
  );

  // Mark messages as read in the cache directly
  const markReadInCache = useCallback(
    (conversationId: string, readBy: string) => {
      dispatch(
        chatApi.util.updateQueryData(
          'getMessages',
          { conversationId, limit: 100 },
          (draft) => {
            for (const msg of draft.data) {
              if (msg.senderId !== readBy && !msg.isRead) {
                msg.isRead = true;
                msg.readAt = new Date().toISOString();
              }
            }
          }
        )
      );
    },
    [dispatch]
  );

  const invalidateConversations = useCallback(() => {
    dispatch(baseApi.util.invalidateTags(['Conversations', 'UnreadCount']));
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      if (globalSocket) {
        globalSocket.disconnect();
        globalSocket = null;
      }
      socketRef.current = null;
      setIsConnected(false);
      return;
    }

    if (!globalSocket) {
      globalSocket = ioClient(SOCKET_URL, {
        // P2 #17: Use function for auth so reconnections use fresh token
        auth: (cb) => cb({ token: accessToken }),
        autoConnect: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 10,
      });
    } else {
      // Update auth for existing socket in case token was refreshed
      globalSocket.auth = (cb: (data: { token: string }) => void) => cb({ token: accessToken });
    }

    refCount++;
    const socket = globalSocket;
    socketRef.current = socket;

    const onConnect = () => {
      setIsConnected(true);
      socket.emit('get_online_status');
    };
    const onDisconnect = () => setIsConnected(false);

    const onOnlineStatuses = (statuses: Record<string, boolean>) => {
      setOnlineUsers(new Set(Object.keys(statuses).filter((id) => statuses[id])));
    };

    // Push message into cache instantly — no network refetch
    const onNewMessage = (message: MessageWithSender) => {
      pushMessageToCache(message);
      // Lightweight: only update conversation list sidebar (last message preview)
      invalidateConversations();
    };

    const onMessageEdited = (message: MessageWithSender) => {
      dispatch(
        chatApi.util.updateQueryData(
          'getMessages',
          { conversationId: message.conversationId, limit: 100 },
          (draft) => {
            const idx = draft.data.findIndex((m) => m.id === message.id);
            if (idx !== -1) {
              draft.data[idx] = message;
            }
          }
        )
      );
    };

    const onMessageDeleted = (message: MessageWithSender) => {
      dispatch(
        chatApi.util.updateQueryData(
          'getMessages',
          { conversationId: message.conversationId, limit: 100 },
          (draft) => {
            const idx = draft.data.findIndex((m) => m.id === message.id);
            if (idx !== -1) {
              draft.data[idx] = message;
            }
          }
        )
      );
      invalidateConversations();
    };

    const onConversationUpdated = () => {
      invalidateConversations();
    };

    const onMessagesRead = (data: { conversationId: string; readBy: string }) => {
      markReadInCache(data.conversationId, data.readBy);
    };

    const onUserOnline = (data: { userId: string }) => {
      setOnlineUsers((prev) => {
        const next = new Set(prev);
        next.add(data.userId);
        return next;
      });
    };

    const onUserOffline = (data: { userId: string }) => {
      setOnlineUsers((prev) => {
        const next = new Set(prev);
        next.delete(data.userId);
        return next;
      });
    };

    const onUserTyping = (data: { conversationId: string; userId: string }) => {
      setTypingUsers((prev) => {
        const next = new Map(prev);
        next.set(data.conversationId, data.userId);
        return next;
      });
    };

    const onUserStopTyping = (data: { conversationId: string }) => {
      setTypingUsers((prev) => {
        const next = new Map(prev);
        next.delete(data.conversationId);
        return next;
      });
    };

    const onMessageReaction = (data: { messageId: string; conversationId: string; userId: string; emoji: string; action: 'added' | 'removed' }) => {
      // Update the message's reactions in cache
      dispatch(
        chatApi.util.updateQueryData(
          'getMessages',
          { conversationId: data.conversationId, limit: 100 },
          (draft) => {
            const msg = draft.data.find((m) => m.id === data.messageId);
            if (msg) {
              if (!msg.reactions) msg.reactions = [];
              if (data.action === 'added') {
                msg.reactions.push({ id: `${data.messageId}-${data.userId}-${data.emoji}`, emoji: data.emoji, userId: data.userId });
              } else {
                msg.reactions = msg.reactions.filter(
                  (r) => !(r.emoji === data.emoji && r.userId === data.userId)
                );
              }
            }
          }
        )
      );
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('new_message', onNewMessage);
    socket.on('message_edited', onMessageEdited);
    socket.on('message_deleted', onMessageDeleted);
    socket.on('message_reaction', onMessageReaction);
    socket.on('conversation_updated', onConversationUpdated);
    socket.on('messages_read', onMessagesRead);
    socket.on('user_online', onUserOnline);
    socket.on('user_offline', onUserOffline);
    socket.on('user_typing', onUserTyping);
    socket.on('user_stop_typing', onUserStopTyping);
    socket.on('online_statuses', onOnlineStatuses);

    if (socket.connected) {
      setIsConnected(true);
      // Re-fetch online statuses on re-mount (e.g. navigating back to inbox)
      socket.emit('get_online_status');
    } else {
      socket.connect();
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('new_message', onNewMessage);
      socket.off('message_edited', onMessageEdited);
      socket.off('message_deleted', onMessageDeleted);
      socket.off('message_reaction', onMessageReaction);
      socket.off('conversation_updated', onConversationUpdated);
      socket.off('messages_read', onMessagesRead);
      socket.off('user_online', onUserOnline);
      socket.off('user_offline', onUserOffline);
      socket.off('user_typing', onUserTyping);
      socket.off('user_stop_typing', onUserStopTyping);
      socket.off('online_statuses', onOnlineStatuses);

      refCount--;
      if (refCount <= 0 && globalSocket) {
        globalSocket.disconnect();
        globalSocket = null;
        refCount = 0;
      }
    };
  }, [isAuthenticated, accessToken, pushMessageToCache, markReadInCache, invalidateConversations]);

  return {
    socket: socketRef.current,
    isConnected,
    onlineUsers,
    typingUsers,
  };
}
